import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useContext } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { PermissionsContext } from './PermissionsContext';
import { AbilityAction, AbilitySubject } from './hooks';

/**
 * A CASL subject *instance* — a GraphQL-shaped object CASL evaluates rule conditions
 * against. `__typename` is constrained to a known subject (so typos are caught); the
 * remaining fields mirror the backend's Mongo-style conditions and are intentionally
 * open.
 */
export type SubjectInstance = { __typename: AbilitySubject } & Record<string, unknown>;

/**
 * Row-level permission check: "can the current user perform `action` on THIS specific
 * entity?" Unlike `usePermission` (a type-level capability check that only sees the
 * subject type), this evaluates rule conditions against a concrete instance, so
 * condition-scoped rules — including inverted `cannot` rules — are honored.
 */
export function useCan(action: AbilityAction, subject: SubjectInstance): boolean {
  const { loading } = useQuery<API.UserGlobalPermissionsQuery>(Queries.user.userGlobalPermissions);
  const ability = useContext(PermissionsContext);
  if (loading || isNil(ability)) {
    return false;
  }
  // Subjects are typed as strings in AbilityType, but CASL's detectSubjectType
  // reads `__typename` at runtime to evaluate conditions.
  return ability.can(action, subject as unknown as AbilitySubject);
}

/** Factories for the subject instances used in row-level permission checks. */
export const permissionSubject = {
  channelMembership: (channelId?: number, userId?: number): SubjectInstance => ({
    __typename: 'ChannelMembership',
    channel: { id: channelId },
    user: { id: userId },
  }),
  workflowTemplate: (id?: string): SubjectInstance => ({ __typename: 'WorkflowTemplate', id }),
  viewTemplate: (workflowTemplateId?: string): SubjectInstance => ({
    __typename: 'ViewTemplate',
    workflowTemplateId,
  }),
};
